import { useEffect, useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { useUserOrg } from '../lib/org';
import { LoadingIndicator } from '../components/LoadingIndicator';
import {
  getSite, listSiteDocuments, listOrgDocuments, listForms, listAllPermits,
  listAllTrainingRecords, listCoshh, listRams, listActiveSignIns,
  type Site, type DocumentRow, type FormRow, type PermitRow,
} from '../lib/db';
import {
  buildSiteChecklist, computeInCharge, groupByPriority,
  getNotRequiredIds, setNotRequired,
  enterSetupMode, exitSetupMode,
  type SetupItem, type SetupStatus,
} from '../data/siteSetupChecklist';

export function SiteSetupChecklist() {
  const { siteId } = useParams<{ siteId: string }>();
  const { org } = useUserOrg();
  const nav = useNavigate();

  const [site, setSite] = useState<Site | null>(null);
  const [orgDocs, setOrgDocs] = useState<DocumentRow[]>([]);
  const [siteDocs, setSiteDocs] = useState<DocumentRow[]>([]);
  const [forms, setForms] = useState<FormRow[]>([]);
  const [permits, setPermits] = useState<PermitRow[]>([]);
  const [hasTraining, setHasTraining] = useState(false);
  const [hasCoshh, setHasCoshh] = useState(false);
  const [hasRams, setHasRams] = useState(false);
  const [hasSignIns, setHasSignIns] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);   // bump to re-read localStorage after toggle

  useEffect(() => {
    if (siteId) enterSetupMode(siteId);
  }, [siteId]);

  useEffect(() => {
    if (!siteId || !org) return;
    let cancelled = false;
    setLoading(true); setError(null);
    (async () => {
      try {
        const [s, od, sd, f, p, tr, co, rm, si] = await Promise.all([
          getSite(siteId),
          listOrgDocuments(org.org_id).catch(() => []),
          listSiteDocuments(siteId).catch(() => []),
          listForms(siteId).catch(() => []),
          listAllPermits(org.org_id).catch(() => []),
          listAllTrainingRecords(org.org_id).catch(() => []),
          listCoshh(org.org_id).catch(() => []),
          listRams(siteId).catch(() => []),
          listActiveSignIns(siteId).catch(() => []),
        ]);
        if (cancelled) return;
        setSite(s); setOrgDocs(od); setSiteDocs(sd); setForms(f);
        setPermits(p); setHasTraining(tr.length > 0); setHasCoshh(co.length > 0);
        setHasRams(rm.length > 0); setHasSignIns(si.length > 0);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [siteId, org]);

  const items = useMemo<SetupItem[]>(() => {
    if (!site || !org) return [];
    const inCharge = computeInCharge(site, org);
    return buildSiteChecklist({ site, org, inCharge });
  }, [site, org]);

  const notRequired = useMemo(() => siteId ? getNotRequiredIds(siteId) : new Set<string>(), [siteId, tick]);

  function statusFor(item: SetupItem): SetupStatus {
    if (notRequired.has(item.id)) return 'not_required';
    const t = item.target;
    if (t.kind === 'document') {
      const found = [...orgDocs, ...siteDocs].some((d) => d.template_id === t.templateId && d.is_current);
      return found ? 'done' : 'not_started';
    }
    if (t.kind === 'rams')              return hasRams ? 'done' : 'not_started';
    if (t.kind === 'coshh')             return hasCoshh ? 'done' : 'not_started';
    if (t.kind === 'tbt')               return forms.some((f) => f.kind === 'toolbox_talk') ? 'done' : 'not_started';
    if (t.kind === 'subbie_induction')  return forms.some((f) => f.kind === 'subcontractor_induction') ? 'done' : 'not_started';
    if (t.kind === 'permit')            return permits.length > 0 ? 'done' : 'not_started';
    if (t.kind === 'training')          return hasTraining ? 'done' : 'not_started';
    if (t.kind === 'sign_in')           return hasSignIns ? 'done' : 'not_started';
    return 'not_started';
  }

  function linkFor(item: SetupItem): string {
    const t = item.target;
    if (t.kind === 'document' && t.templateId === 'construction_phase_plan') {
      return `/documents/site/${siteId}/template/construction_phase_plan`;
    }
    if (t.kind === 'document') {
      // Most CDM / project documents are site-scoped. Org-scoped templates
      // (H&S Policy, Sub-contractor PQQ, CDM Compliance Statement) are
      // a smaller set — route them via the org template form.
      const orgScoped = new Set([
        'hs_policy_statement', 'subcontractor_pqq', 'cdm_compliance_statement',
        'first_aid_policy', 'manual_handling_policy', 'driving_for_work_policy',
      ]);
      return orgScoped.has(t.templateId)
        ? `/documents/org/template/${t.templateId}`
        : `/documents/site/${siteId}/template/${t.templateId}`;
    }
    if (t.kind === 'rams')              return `/sites/${siteId}/rams`;
    if (t.kind === 'coshh')             return `/registers/coshh`;
    if (t.kind === 'tbt')               return `/sites/${siteId}/forms/toolbox_talk`;
    if (t.kind === 'subbie_induction')  return `/sites/${siteId}/forms/subcontractor_induction`;
    if (t.kind === 'permit')            return `/sites/${siteId}/permits`;
    if (t.kind === 'training')          return `/registers/training`;
    if (t.kind === 'sign_in')           return `/sites/${siteId}`;
    if (t.kind === 'external_link')     return t.href;
    return '/';
  }

  function toggleNR(item: SetupItem) {
    if (!siteId) return;
    const isNR = notRequired.has(item.id);
    setNotRequired(siteId, item.id, !isNR);
    setTick((n) => n + 1);
  }

  const grouped = useMemo(() => groupByPriority(items), [items]);

  // Counts for header
  const totals = useMemo(() => {
    let done = 0, todo = 0;
    for (const it of items) {
      const st = statusFor(it);
      if (st === 'done' || st === 'not_required') done++; else todo++;
    }
    return { done, todo, total: items.length };
  }, [items, orgDocs, siteDocs, forms, permits, hasTraining, hasCoshh, hasRams, hasSignIns, notRequired]);

  if (loading) return <AppShell title="Site setup" back={`/sites/${siteId ?? ''}`}><LoadingIndicator /></AppShell>;
  if (error)   return <AppShell title="Site setup" back={`/sites/${siteId ?? ''}`}><div className="banner banner-error">{error}</div></AppShell>;
  if (!site || !org) return <AppShell title="Site setup" back="/"><div className="banner banner-error">Site not found.</div></AppShell>;

  return (
    <AppShell title="Site setup" back={`/sites/${siteId}`}>
      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: 0, fontWeight: 600 }}>{site.name}</p>
        <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
          Everything this job needs. Tick items off by filling them in. If something genuinely doesn't apply, tap "I don't want this" to hide it.
        </p>
        <p style={{ margin: '10px 0 0', fontWeight: 600, color: totals.todo === 0 ? 'var(--success, #2F6B47)' : 'var(--ink)' }}>
          {totals.todo === 0 ? '✓ Site is fully set up.' : `${totals.todo} of ${totals.total} still to do.`}
        </p>
      </div>

      {(['required','recommended','optional'] as const).map((p) => {
        const list = grouped[p];
        if (list.length === 0) return null;
        const heading = p === 'required' ? 'Required' : p === 'recommended' ? 'Recommended' : 'When applicable';
        return (
          <div key={p} style={{ marginBottom: 18 }}>
            <h3 className="section-title" style={{ marginBottom: 8 }}>
              <span className={`setup-dot setup-dot-${p}`} />
              {heading}
            </h3>
            {list.map((item) => {
              const st = statusFor(item);
              const link = linkFor(item);
              const isExternal = item.target.kind === 'external_link';
              const isNR = st === 'not_required';
              return (
                <div key={item.id} className={`setup-row${isNR ? ' setup-row-nr' : ''}`}>
                  {/* External-link items (F10 on HSE portal, etc.) must use a
                      real <a> with target="_blank" so the row actually opens
                      the HSE site in a new tab — a <Link to="#"> just sat
                      there doing nothing. */}
                  {isExternal ? (
                    <a
                      href={isNR ? undefined : link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="setup-row-main"
                      onClick={(e) => { if (isNR) e.preventDefault(); }}
                    >
                      <div>
                        <p className="setup-row-title">{item.title}</p>
                        <p className="setup-row-why">{item.why}</p>
                      </div>
                      <span className={`setup-status setup-status-${st}`}>{statusLabel(st)}</span>
                    </a>
                  ) : (
                    <Link
                      to={link}
                      className="setup-row-main"
                      onClick={(e) => { if (isNR) e.preventDefault(); }}
                    >
                      <div>
                        <p className="setup-row-title">{item.title}</p>
                        <p className="setup-row-why">{item.why}</p>
                      </div>
                      <span className={`setup-status setup-status-${st}`}>{statusLabel(st)}</span>
                    </Link>
                  )}
                  <button type="button"
                    className="setup-row-nr-btn"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleNR(item); }}>
                    {isNR ? 'Add it back' : "I don't want this"}
                  </button>
                </div>
              );
            })}
          </div>
        );
      })}

      <div className="btn-stack" style={{ marginTop: 24 }}>
        <button type="button" className="btn btn-primary"
          onClick={() => { exitSetupMode(); nav('/'); }}>
          {totals.todo === 0 ? '✓ Finished — go to home' : 'Done for now — go to home'}
        </button>
        <p className="muted" style={{ fontSize: 12, textAlign: 'center', margin: '8px 0 0' }}>
          You can come back here any time from the Active site banner.
        </p>
      </div>
    </AppShell>
  );
}

function statusLabel(s: SetupStatus): string {
  switch (s) {
    case 'done':         return '✓ Done';
    case 'in_progress':  return 'In progress';
    case 'not_started':  return 'To do';
    case 'not_required': return 'Skipped';
  }
}
